import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonBoolean, jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"

/** A channel that reaches the user away from Mux. */
export type ContactChannel = "email" | "sms"

/** The user's email address or phone number for chat notifications. */
export type NotificationContact = {
  channel: ContactChannel
  address: string
  verified: boolean
  /** Messages say what happened; otherwise they only link to the chat. */
  details: boolean
  /** Why messages stopped, such as the number replying STOP. */
  disabledReason?: string
  /** When the code that verifies an unverified contact was sent. */
  codeSentAt?: number
}

export type NotificationContacts = {
  /** Channels this Mux can send by. */
  available: Record<ContactChannel, boolean>
  contacts: NotificationContact[]
}

const invalid = "Kettle returned an invalid notification contact"

function decodeContact(value: unknown): NotificationContact {
  const contact = jsonObject(value, invalid)
  const channel = jsonString(contact.channel, invalid)
  if (channel !== "email" && channel !== "sms") throw new Error(invalid)
  return {
    channel,
    address: jsonString(contact.address, invalid),
    verified: jsonBoolean(contact.verified, invalid),
    details: jsonBoolean(contact.details, invalid),
    disabledReason: typeof contact.disabledReason === "string" && contact.disabledReason ? contact.disabledReason : undefined,
    codeSentAt: typeof contact.codeSentAt === "number" ? contact.codeSentAt : undefined,
  }
}

export async function listNotificationContacts(signal?: AbortSignal): Promise<NotificationContacts> {
  return expectJson(await controlApiFetch("/api/notification-contacts", { signal }), (value) => {
    const body = jsonObject(value, invalid)
    const available = jsonObject(body.available, invalid)
    return {
      available: { email: available.email === true, sms: available.sms === true },
      contacts: jsonArray(body.contacts, invalid).map(decodeContact),
    }
  })
}

/** Sets the address on channel; a new one gets a code to verify it. */
export async function saveNotificationContact(channel: ContactChannel, input: { address: string; details?: boolean }) {
  return expectJson(await controlApiFetch(`/api/notification-contacts/${channel}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  }), decodeContact)
}

export async function verifyNotificationContact(channel: ContactChannel, code: string) {
  return expectJson(await controlApiFetch(`/api/notification-contacts/${channel}/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  }), decodeContact)
}

export async function deleteNotificationContact(channel: ContactChannel) {
  await expectResponse(await controlApiFetch(`/api/notification-contacts/${channel}`, { method: "DELETE" }))
}
